import React, { useState, useEffect, useCallback } from "react";
import { getRequest, putRequest } from "../../utils/httpRequest";
import { colors, pageHeaderStyles } from "../AgenticPaymentAirdrop/styles";

// Where "Fund with USDC" deposits land, per chain. Same flow as the Genesis
// USDC receiver card: current value, inputs, confirm, revert to env default.
const CHAINS = [
  {
    key: "polygon", name: "Polygon", re: /^0x[0-9a-fA-F]{40}$/,
    label: "Polygon receiver (0x + 40 hex)", placeholder: "0x…", error: "Must be 0x + 40 hex chars",
  },
  {
    key: "stellar", name: "Stellar", re: /^G[A-Z2-7]{55}$/,
    label: "Stellar receiver (G…, 56 chars)", placeholder: "G…", error: "Must be G + 55 characters",
    hint: "Must hold a USDC trustline.",
  },
];

const card = {
  background: colors.white, border: `1px solid ${colors.border}`,
  borderRadius: "12px", padding: "20px", marginBottom: "20px",
};
const label = { display: "block", fontSize: "13px", fontWeight: 600, color: "#555", marginBottom: "6px" };
const input = {
  width: "100%", padding: "10px 12px", border: "1px solid #ccc", borderRadius: "6px",
  fontSize: "14px", boxSizing: "border-box", fontFamily: "monospace",
};
const btn = (bg, disabled) => ({
  display: "inline-flex", alignItems: "center", justifyContent: "center", minHeight: "38px",
  background: bg, color: "#fff", border: "none", borderRadius: "6px", padding: "9px 18px",
  fontSize: "13px", fontWeight: 700, cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.6 : 1,
});
const notice = (ok) => ({
  background: ok ? "#d4edda" : "#f8d7da", color: ok ? "#155724" : "#721c24",
  border: `1px solid ${ok ? "#c3e6cb" : "#f5c6cb"}`, borderRadius: "6px",
  padding: "10px 12px", fontSize: "13px", marginTop: "12px",
});

const UsdcDepositSettings = ({ pageUrl = "/admin/usdc_deposit_settings" }) => {
  const url = (action) => `${pageUrl.replace(/\/$/, "")}/${action}`;

  const [receivers, setReceivers] = useState({});
  const [defaults, setDefaults] = useState({});
  const [values, setValues] = useState({ polygon: "", stellar: "" });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null); // { ok, text }
  const [revertField, setRevertField] = useState(null);
  const [reverting, setReverting] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await getRequest(url("usdc_deposit_receivers"));
      if (res && res.success) {
        setReceivers((res.data && res.data.receivers) || {});
        setDefaults((res.data && res.data.defaults) || {});
      } else {
        setMsg({ ok: false, text: (res && res.error) || "Could not load the receiver wallets." });
      }
    } catch (err) {
      setMsg({ ok: false, text: err?.data?.error || "Could not load the receiver wallets." });
    }
  }, [pageUrl]);

  useEffect(() => { load(); }, [load]);

  const trimmed = (key) => values[key].trim();
  const invalid = (c) => trimmed(c.key) && !c.re.test(trimmed(c.key));
  const anyInvalid = CHAINS.some(invalid);
  const nothingToSave = CHAINS.every((c) => !trimmed(c.key));

  const save = async () => {
    setSaving(true);
    setMsg(null);
    const body = {};
    CHAINS.forEach((c) => { if (trimmed(c.key)) body[c.key] = trimmed(c.key); });
    try {
      const res = await putRequest(url("update_usdc_deposit_receivers"), body);
      if (res && res.success) {
        setReceivers((res.data && res.data.receivers) || receivers);
        setValues({ polygon: "", stellar: "" });
        setConfirmOpen(false);
        setMsg({ ok: true, text: "USDC deposit receiver wallet(s) updated." });
      } else {
        setMsg({ ok: false, text: (res && res.error) || "Update failed." });
      }
    } catch (err) {
      setMsg({ ok: false, text: err?.data?.error || "Update failed. Please try again." });
    } finally {
      setSaving(false);
    }
  };

  const revert = async () => {
    const field = revertField;
    if (!field) return;
    setReverting(true);
    setMsg(null);
    try {
      const res = await putRequest(url("update_usdc_deposit_receivers"), { [field]: null });
      if (res && res.success) {
        setReceivers((res.data && res.data.receivers) || {});
        setRevertField(null);
        setMsg({ ok: true, text: `${field === "polygon" ? "Polygon" : "Stellar"} reverted to env default.` });
      } else {
        setMsg({ ok: false, text: (res && res.error) || "Revert failed." });
      }
    } catch (err) {
      setMsg({ ok: false, text: err?.data?.error || "Revert failed. Please try again." });
    } finally {
      setReverting(false);
    }
  };

  const sourceBadge = (text, bg, color, border) => (
    <span style={{ fontSize: "10px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.4px",
                   background: bg, color, border: `1px solid ${border}`, borderRadius: "10px", padding: "1px 7px", marginLeft: "6px" }}>
      {text}
    </span>
  );

  const renderCurrent = (chain) => {
    const set = receivers[chain];
    const def = defaults[chain];
    if (set) {
      return <span><code style={{ fontFamily: "monospace", color: colors.primary, wordBreak: "break-all" }}>{set}</code>{sourceBadge("custom", "#d1ecf1", "#0c5460", "#bee5eb")}</span>;
    }
    if (def) {
      return <span><code style={{ fontFamily: "monospace", color: colors.gray700, wordBreak: "break-all" }}>{def}</code>{sourceBadge("env default", "#e9ecef", "#495057", "#dee2e6")}</span>;
    }
    return <span style={{ color: "#c0392b", fontStyle: "italic" }}>not configured</span>;
  };

  return (
    <div style={{ paddingBottom: "24px", maxWidth: "760px" }}>
      <div style={{ ...pageHeaderStyles.container, marginBottom: "20px", borderRadius: "12px" }}>
        <h1 style={pageHeaderStyles.title}>USDC Deposit Wallets</h1>
      </div>

      <div style={card}>
        <div style={{ fontSize: "15px", fontWeight: 700, color: colors.primary, marginBottom: "4px" }}>USDC deposit receiver wallets</div>
        <div style={{ fontSize: "12px", color: colors.gray600, marginBottom: "14px" }}>
          Where Fund with USDC deposits are received, per chain. The customer is credited the same amount in USDb. <strong style={{ color: "#932419" }}>This controls where real funds land</strong>: double-check before saving. Leave a field blank to keep the current value.
        </div>

        <div style={{ background: colors.gray100 || "#f7f8fa", borderRadius: "8px", padding: "12px 14px", marginBottom: "16px", fontSize: "13px" }}>
          {CHAINS.map((c, idx) => (
            <div key={c.key} style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap", marginBottom: idx === 0 ? "6px" : 0 }}>
              <span>Current, <strong>{c.name}</strong>: {renderCurrent(c.key)}</span>
              {receivers[c.key] && revertField !== c.key && (
                <button type="button" onClick={() => { setMsg(null); setRevertField(c.key); }}
                        style={{ background: "none", border: "none", color: "#c0392b", cursor: "pointer", fontSize: "12px", textDecoration: "underline", padding: 0 }}>
                  Revert to env default
                </button>
              )}
              {revertField === c.key && (
                <span style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
                  <span style={{ color: "#9a3412", fontSize: "12px" }}>Clear {c.name} back to env default?</span>
                  <button type="button" disabled={reverting} onClick={revert}
                          style={{ background: "#c0392b", color: "#fff", border: "none", borderRadius: "5px", padding: "4px 10px", fontSize: "12px", fontWeight: 600, cursor: reverting ? "not-allowed" : "pointer", opacity: reverting ? 0.6 : 1 }}>
                    {reverting ? "…" : "Confirm revert"}
                  </button>
                  <button type="button" disabled={reverting} onClick={() => setRevertField(null)}
                          style={{ background: "none", border: "none", color: "#666", cursor: "pointer", fontSize: "12px", padding: 0 }}>Cancel</button>
                </span>
              )}
            </div>
          ))}
        </div>

        <div style={{ display: "flex", gap: "14px", flexWrap: "wrap", marginBottom: "16px" }}>
          {CHAINS.map((c) => (
            <div key={c.key} style={{ flex: "1 1 260px" }}>
              <label style={label}>{c.label}</label>
              <input style={{ ...input, borderColor: invalid(c) ? "#dc3545" : "#ccc" }} value={values[c.key]}
                     onChange={(e) => setValues({ ...values, [c.key]: e.target.value })}
                     placeholder={receivers[c.key] || c.placeholder} />
              {invalid(c)
                ? <div style={{ color: "#dc3545", fontSize: "11px", marginTop: "4px" }}>{c.error}</div>
                : c.hint && <div style={{ color: colors.gray600, fontSize: "11px", marginTop: "4px" }}>{c.hint}</div>}
            </div>
          ))}
        </div>

        {!confirmOpen ? (
          <button type="button"
                  onClick={() => { setMsg(null); setConfirmOpen(true); }}
                  disabled={nothingToSave || anyInvalid}
                  style={btn(colors.primary, nothingToSave || anyInvalid)}>
            Save receiver wallet(s)
          </button>
        ) : (
          <div style={{ background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: "8px", padding: "14px 16px" }}>
            <div style={{ fontWeight: 700, color: "#9a3412", marginBottom: "8px" }}>Confirm, deposits will be received at:</div>
            {CHAINS.filter((c) => trimmed(c.key)).map((c) => (
              <div key={c.key} style={{ fontSize: "13px", marginBottom: "4px", wordBreak: "break-all" }}>{c.name} → <code>{trimmed(c.key)}</code></div>
            ))}
            <div style={{ display: "flex", gap: "10px", marginTop: "12px" }}>
              <button type="button" onClick={() => setConfirmOpen(false)} disabled={saving}
                      style={{ display: "inline-flex", alignItems: "center", minHeight: "38px", background: "#e2e3e5", color: "#333", border: "none", borderRadius: "6px", padding: "9px 16px", fontSize: "13px", fontWeight: 600, cursor: "pointer" }}>
                Cancel
              </button>
              <button type="button" onClick={save} disabled={saving} style={btn("#c0392b", saving)}>
                {saving ? "Saving…" : "Confirm & save"}
              </button>
            </div>
          </div>
        )}
        {msg && <div style={notice(msg.ok)}>{msg.text}</div>}
      </div>
    </div>
  );
};

export default UsdcDepositSettings;
